// (c) chaehongee. Confidential and proprietary.

/**
 * Azure Application Insights telemetry service for frontend.
 *
 * Provides page view tracking, custom event tracking, and performance monitoring
 * for the React frontend.
 */

import { ApplicationInsights } from '@microsoft/applicationinsights-web';

class TelemetryService {
  private appInsights: ApplicationInsights | null = null;
  private isInitialized = false;

  /**
   * Initialize Azure Application Insights.
   * Should be called once at application startup.
   */
  initialize() {
    const connectionString = import.meta.env.VITE_APPLICATIONINSIGHTS_CONNECTION_STRING;

    if (!connectionString) {
      console.warn('Azure Application Insights connection string not found. Telemetry disabled.');
      return;
    }

    try {
      this.appInsights = new ApplicationInsights({
        config: {
          connectionString,
          enableAutoRouteTracking: true, // Automatically track route changes
          enableCorsCorrelation: true, // Enable correlation headers for distributed tracing
          enableRequestHeaderTracking: true,
          enableResponseHeaderTracking: true,
          enableAjaxPerfTracking: true, // Track AJAX performance
          maxAjaxCallsPerView: -1, // Track all AJAX calls
          disableFetchTracking: false,
          disableAjaxTracking: false,
          disableExceptionTracking: false,
          disableTelemetry: false,
        }
      });

      this.appInsights.loadAppInsights();
      this.appInsights.trackPageView(); // Track initial page view
      this.isInitialized = true;

      console.log('Azure Application Insights initialized successfully');
    } catch (error) {
      console.error('Failed to initialize Azure Application Insights:', error);
    }
  }

  /**
   * Track a custom event.
   */
  trackEvent(name: string, properties?: Record<string, any>) {
    if (!this.isInitialized || !this.appInsights) return;

    try {
      this.appInsights.trackEvent({ name }, properties);
    } catch (error) {
      console.error('Failed to track event:', error);
    }
  }

  /**
   * Track a custom metric.
   */
  trackMetric(name: string, value: number, properties?: Record<string, any>) {
    if (!this.isInitialized || !this.appInsights) return;

    try {
      this.appInsights.trackMetric({ name, average: value }, properties);
    } catch (error) {
      console.error('Failed to track metric:', error);
    }
  }

  /**
   * Track an exception.
   */
  trackException(error: Error, properties?: Record<string, any>) {
    if (!this.isInitialized || !this.appInsights) return;

    try {
      this.appInsights.trackException({ exception: error }, properties);
    } catch (err) {
      console.error('Failed to track exception:', err);
    }
  }

  /**
   * Track a page view.
   */
  trackPageView(name?: string, properties?: Record<string, any>) {
    if (!this.isInitialized || !this.appInsights) return;

    try {
      this.appInsights.trackPageView({ name, properties });
    } catch (error) {
      console.error('Failed to track page view:', error);
    }
  }

  /**
   * Set user context for correlation.
   */
  setUser(userId: string, accountId?: string) {
    if (!this.isInitialized || !this.appInsights) return;

    try {
      this.appInsights.setAuthenticatedUserContext(userId, accountId, true);
    } catch (error) {
      console.error('Failed to set user context:', error);
    }
  }

  /**
   * Clear user context.
   */
  clearUser() {
    if (!this.isInitialized || !this.appInsights) return;

    try {
      this.appInsights.clearAuthenticatedUserContext();
    } catch (error) {
      console.error('Failed to clear user context:', error);
    }
  }

  /**
   * Track canvas render performance.
   */
  trackCanvasRender(nodeCount: number, zoomLevel: number, duration: number) {
    this.trackMetric('canvas.render.duration', duration, {
      node_count: nodeCount,
      zoom_level: zoomLevel,
    });
  }

  /**
   * Track image generation event.
   */
  trackImageGeneration(toolName: string, success: boolean, duration?: number) {
    this.trackEvent(success ? 'image_generated' : 'generation_failed', {
      tool_name: toolName,
      success,
      duration_ms: duration,
    });
  }

  /**
   * Track user interaction event.
   */
  trackInteraction(action: string, properties?: Record<string, any>) {
    this.trackEvent('user_interaction', {
      action,
      ...properties,
    });
  }

  /**
   * Flush all pending telemetry.
   */
  flush() {
    if (!this.isInitialized || !this.appInsights) return;

    try {
      this.appInsights.flush();
    } catch (error) {
      console.error('Failed to flush telemetry:', error);
    }
  }
}

// Export singleton instance
export const telemetry = new TelemetryService();
